import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDate } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { BookOpen, Plus, Trash2, Pencil, Loader2, Check, CheckCircle2, Replace, Users } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { isManagement } from '../lib/roles';

/** "28/07/2026 18:42" a partir de un ISO. */
function fechaHora(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${formatDate(iso.slice(0, 10))} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return iso;
  }
}

/** Diálogo de alta/edición: área, título, descripción. */
function ProtocoloDialog({ open, onOpenChange, inicial, areas, onSaved }) {
  const esEdicion = !!inicial;
  const [form, setForm] = useState({ area: '', titulo: '', descripcion: '' });
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      area: inicial?.area || '',
      titulo: inicial?.titulo || '',
      descripcion: inicial?.descripcion || '',
    });
  }, [open, inicial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.area.trim()) return toast.error('Pon un área (Secretaría, Quirófano…)');
    if (!form.titulo.trim()) return toast.error('Pon un título');
    setGuardando(true);
    try {
      const payload = {
        area: form.area.trim(),
        titulo: form.titulo.trim(),
        descripcion: form.descripcion,
      };
      const res = esEdicion
        ? await api.put(`/api/protocolos/${inicial.id}`, payload)
        : await api.post('/api/protocolos', payload);
      toast.success(esEdicion ? 'Protocolo actualizado' : 'Protocolo creado');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{esEdicion ? 'Editar protocolo' : 'Nuevo protocolo'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Área</Label>
            {/* Texto libre, pero con las áreas que ya existen a un clic: así no
                acaban conviviendo "Quirófano" y "quirofano". */}
            <Input
              list="protocolo-areas"
              value={form.area}
              onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))}
              placeholder="Secretaría, Quirófano, Enfermería…"
              data-testid="protocolo-area"
            />
            <datalist id="protocolo-areas">
              {areas.map((a) => <option key={a} value={a} />)}
            </datalist>
          </div>
          <div>
            <Label>Título</Label>
            <Input
              value={form.titulo}
              onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
              placeholder="Qué pauta es"
              data-testid="protocolo-titulo"
            />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea
              rows={8}
              value={form.descripcion}
              onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
              placeholder="Cómo se hace, quién lo hace y qué hay que tener en cuenta."
              data-testid="protocolo-descripcion"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
              Cancelar
            </Button>
            <Button type="submit" disabled={guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="protocolo-guardar">
              {guardando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Diálogo para escribir una pauta, nueva o sustituyendo a otra. */
function PautaDialog({ open, onOpenChange, protocolo, sustituyendo, onSaved }) {
  const [texto, setTexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  // Notificar al equipo (aviso + cartel) y a QUIÉN se le exige confirmar.
  const [notificar, setNotificar] = useState(true);
  const [modo, setModo] = useState('todos'); // 'todos' | 'algunos'
  const [usuarios, setUsuarios] = useState([]);
  const [elegidos, setElegidos] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    setTexto(''); setNotificar(true); setModo('todos'); setElegidos(new Set());
    api.get('/api/protocolos/usuarios').then((r) => setUsuarios(r.data?.usuarios || [])).catch(() => setUsuarios([]));
  }, [open]);

  const alternar = (id) => setElegidos((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!texto.trim()) return toast.error('Escribe la pauta');
    if (modo === 'algunos' && elegidos.size === 0) return toast.error('Elige a quién debe confirmarla');
    setGuardando(true);
    try {
      await api.post(`/api/protocolos/${protocolo.id}/pautas`, {
        texto: texto.trim(),
        sustituye_a: sustituyendo?.id || null,
        notificar,
        destinatarios: modo === 'algunos' ? Array.from(elegidos) : null,
      });
      const aQuien = modo === 'algunos' ? `${elegidos.size} persona${elegidos.size === 1 ? '' : 's'}` : 'el equipo';
      toast.success(sustituyendo
        ? `Pauta sustituida · ${aQuien} la verá en rojo hasta confirmarla`
        : `Pauta añadida · ${aQuien} la verá en rojo hasta confirmarla`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{sustituyendo ? 'Sustituir pauta' : 'Nueva pauta'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {sustituyendo && (
            <div className="rounded-lg bg-muted/50 border border-border p-3">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                Sustituye a
              </div>
              <div className="text-sm line-through text-muted-foreground whitespace-pre-wrap">
                {sustituyendo.texto}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Las dos convivirán: la antigua tachada y la nueva en rojo. A cada
                persona le desaparecerá la antigua cuando confirme la nueva.
              </p>
            </div>
          )}
          <div>
            <Label>{sustituyendo ? 'Nueva redacción' : 'Pauta'}</Label>
            <Textarea
              rows={5}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Qué hay que hacer, quién lo hace y desde cuándo."
              data-testid="pauta-texto"
            />
          </div>

          {/* Notificar al equipo (2026-08-27, dirección) */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-[#2E6E5E]"
              checked={notificar} onChange={(e) => setNotificar(e.target.checked)}
              data-testid="pauta-notificar" />
            Notificar al equipo (les salta aviso y cartel al conectarse)
          </label>

          {/* Confirmación obligatoria: todos o personas concretas */}
          <div>
            <Label>¿Quién debe confirmar que la ha leído?</Label>
            <div className="flex gap-1.5 mt-1 mb-2">
              {[['todos', 'Todo el equipo'], ['algunos', 'Personas concretas']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setModo(v)}
                  data-testid={`pauta-modo-${v}`}
                  className={`px-3 py-1 rounded-full border text-xs font-medium ${
                    modo === v ? 'border-[#2E6E5E] bg-[#2E6E5E]/10 text-[#2E6E5E]' : 'border-border'}`}>
                  {l}
                </button>
              ))}
            </div>
            {modo === 'algunos' && (
              <div className="max-h-44 overflow-y-auto rounded-md border border-border divide-y divide-border">
                {usuarios.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">Cargando personal…</div>
                ) : usuarios.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-[rgba(46,110,94,0.05)]"
                    data-testid={`pauta-dest-${u.id}`}>
                    <input type="checkbox" className="w-4 h-4 accent-[#2E6E5E]"
                      checked={elegidos.has(u.id)} onChange={() => alternar(u.id)} />
                    <span>{u.name}</span>
                    {u.role && <span className="text-xs text-muted-foreground">· {u.role}</span>}
                  </label>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
              Cancelar
            </Button>
            <Button type="submit" disabled={guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="pauta-guardar">
              {guardando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Quién ha confirmado una pauta y quién no. Solo dirección. */
function LecturasDialog({ pauta, onOpenChange }) {
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    if (!pauta) { setDatos(null); return; }
    api.get(`/api/protocolos/pautas/${pauta.id}/lecturas`)
      .then((r) => setDatos(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'No se pudo cargar el registro'));
  }, [pauta]);

  return (
    <Dialog open={!!pauta} onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Quién ha leído esta pauta</DialogTitle></DialogHeader>
        <div className="text-sm whitespace-pre-wrap bg-muted/50 border border-border rounded-lg p-3">
          {pauta?.texto}
        </div>
        {datos === null ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : (
          <div className="space-y-4 max-h-[50vh] overflow-y-auto">
            <div>
              <div className="text-xs font-semibold text-emerald-700 mb-1.5">
                Confirmada por {datos.confirmadas.length}
              </div>
              {datos.confirmadas.length === 0 ? (
                <p className="text-xs text-muted-foreground">Todavía nadie.</p>
              ) : (
                <ul className="space-y-1">
                  {datos.confirmadas.map((c) => (
                    <li key={c.id} className="text-xs flex justify-between gap-3 border-b border-border/60 pb-1">
                      <span className="font-medium">{c.user_name}</span>
                      <span className="text-muted-foreground tabular-nums">{fechaHora(c.confirmado_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-xs font-semibold text-red-700 mb-1.5">
                Sin leer: {datos.faltan.length}
              </div>
              {datos.faltan.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nadie: la ha leído todo el equipo.</p>
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {datos.faltan.map((f) => (
                    <li key={f.id} className="text-[11px] rounded-full border border-red-300 bg-red-50 text-red-900 px-2 py-0.5">
                      {f.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Una pauta con su estado: pendiente (rojo), confirmada o sustituida (tachada). */
function Pauta({ pauta, puedeEditar, onConfirmada, onSustituir, onBorrar, onVerLecturas }) {
  const [confirmando, setConfirmando] = useState(false);

  const confirmar = async () => {
    setConfirmando(true);
    try {
      await api.post(`/api/protocolos/pautas/${pauta.id}/confirmar`);
      toast.success('Confirmado · queda registrado que la has leído');
      onConfirmada?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo confirmar');
    } finally {
      setConfirmando(false);
    }
  };

  if (pauta.sustituida) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3" data-testid="pauta-sustituida">
        <div className="flex items-center gap-1.5 mb-1">
          <Replace className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Sustituida{pauta.sustituida_at ? ` el ${formatDate(pauta.sustituida_at.slice(0, 10))}` : ''}
          </span>
        </div>
        <p className="text-sm line-through text-muted-foreground whitespace-pre-wrap">{pauta.texto}</p>
        <p className="text-[11px] text-muted-foreground mt-1.5">
          Desaparecerá cuando confirmes la pauta nueva.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border p-3 ${
        pauta.pendiente ? 'border-red-300 bg-red-50/40' : 'border-border bg-background'
      }`}
      data-testid="pauta-row"
    >
      <div className="flex items-start gap-2">
        {pauta.pendiente && (
          <span
            className="mt-1 shrink-0 w-2 h-2 rounded-full bg-red-600"
            title="Sin leer"
            data-testid="pauta-badge"
            aria-label="Pendiente de lectura"
          />
        )}
        <p className="text-sm flex-1 min-w-0 whitespace-pre-wrap">{pauta.texto}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2.5">
        {pauta.pendiente ? (
          <Button
            size="sm"
            className="h-8 bg-red-600 hover:bg-red-700 text-white"
            onClick={confirmar}
            disabled={confirmando}
            data-testid="pauta-confirmar"
          >
            {confirmando
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <><Check className="w-3.5 h-3.5 mr-1" /> Confirmar lectura</>}
          </Button>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700" data-testid="pauta-confirmada">
            <CheckCircle2 className="w-3.5 h-3.5" /> Leída
          </span>
        )}
        <span className="text-[11px] text-muted-foreground">
          {pauta.created_by_name} · {formatDate((pauta.created_at || '').slice(0, 10))}
        </span>
        {puedeEditar && (
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => onVerLecturas(pauta)} data-testid="pauta-lecturas">
              <Users className="w-3.5 h-3.5 mr-1" /> Quién la ha leído
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onSustituir(pauta)} data-testid="pauta-sustituir">
              <Replace className="w-3.5 h-3.5 mr-1" /> Sustituir
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => onBorrar(pauta)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProtocolosPage() {
  const { user } = useAuth();
  const puedeEditar = isManagement(user);
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [area, setArea] = useState('todas');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  // Pautas: alta, sustitución y registro de lecturas.
  const [pautaEn, setPautaEn] = useState(null);        // protocolo al que se le añade
  const [sustituyendo, setSustituyendo] = useState(null);
  const [verLecturas, setVerLecturas] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/api/protocolos');
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar los protocolos');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const areas = useMemo(
    () => [...new Set(items.map((p) => p.area).filter(Boolean))].sort(),
    [items],
  );
  const lista = useMemo(
    () => (area === 'todas' ? items : items.filter((p) => p.area === area)),
    [items, area],
  );

  const borrar = async (p) => {
    if (!window.confirm(`¿Borrar el protocolo "${p.titulo}" y todas sus pautas?`)) return;
    try {
      await api.delete(`/api/protocolos/${p.id}`);
      toast.success('Protocolo borrado');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const borrarPauta = async (pauta) => {
    if (!window.confirm('¿Borrar esta pauta? Se pierde el registro de quién la había leído.')) return;
    try {
      await api.delete(`/api/protocolos/pautas/${pauta.id}`);
      toast.success('Pauta borrada');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const totalPendientes = useMemo(
    () => items.reduce((n, p) => n + (p.pendientes || 0), 0),
    [items],
  );

  return (
    <div className="space-y-5" data-testid="protocolos-page">
      <PageHeader
        title="Protocolos"
        subtitle={
          totalPendientes > 0
            ? `${totalPendientes} ${totalPendientes === 1 ? 'pauta sin leer' : 'pautas sin leer'} · ${items.length} ${items.length === 1 ? 'protocolo' : 'protocolos'}`
            : `${items.length} ${items.length === 1 ? 'protocolo' : 'protocolos'}${areas.length ? ` · ${areas.length} áreas` : ''}`
        }
        actions={puedeEditar && (
          <Button
            onClick={() => { setEditando(null); setDialogOpen(true); }}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="protocolo-nuevo"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuevo protocolo
          </Button>
        )}
      />

      {areas.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="protocolos-areas">
          {['todas', ...areas].map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setArea(a)}
              className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                area === a
                  ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]'
                  : 'bg-background text-muted-foreground border-border hover:text-foreground'
              }`}
            >
              {a === 'todas' ? 'Todas' : a}
            </button>
          ))}
        </div>
      )}

      {cargando ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : lista.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="Sin protocolos todavía"
          description={puedeEditar
            ? 'Escribe aquí las pautas que acordéis en las reuniones: quedan para todo el equipo y no se pierden.'
            : 'Cuando dirección apunte las pautas de las reuniones, aparecerán aquí.'}
        />
      ) : (
        <div className="space-y-3">
          {lista.map((p) => (
            <div key={p.id} className="cjo-card p-4" data-testid="protocolo-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-block rounded-full bg-[rgba(46,110,94,0.12)] text-[#13231E] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                      {p.area}
                    </span>
                    {/* Badge rojo con lo que le queda por leer a esta persona. */}
                    {p.pendientes > 0 && (
                      <span
                        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold"
                        data-testid="protocolo-badge"
                        title={`${p.pendientes} pauta(s) sin leer`}
                      >
                        {p.pendientes}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-[#13231E] mt-1.5">{p.titulo}</h3>
                </div>
                <div className="flex gap-1 shrink-0">
                  {puedeEditar && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSustituyendo(null); setPautaEn(p); }}
                        data-testid="pauta-nueva"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Pauta
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditando(p); setDialogOpen(true); }}
                        data-testid="protocolo-editar"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => borrar(p)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {p.descripcion && (
                <p className="text-sm text-foreground/80 mt-2 whitespace-pre-wrap">{p.descripcion}</p>
              )}

              {(p.pautas || []).length > 0 && (
                <div className="space-y-2 mt-3">
                  {p.pautas.map((pauta) => (
                    <Pauta
                      key={pauta.id}
                      pauta={pauta}
                      puedeEditar={puedeEditar}
                      onConfirmada={cargar}
                      onSustituir={(pa) => { setSustituyendo(pa); setPautaEn(p); }}
                      onBorrar={borrarPauta}
                      onVerLecturas={setVerLecturas}
                    />
                  ))}
                </div>
              )}

              <div className="text-[11px] text-muted-foreground mt-3">
                {p.updated_by_name || p.created_by_name} · {formatDate((p.updated_at || p.created_at || '').slice(0, 10))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ProtocoloDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        inicial={editando}
        areas={areas}
        onSaved={cargar}
      />
      <PautaDialog
        open={!!pautaEn}
        onOpenChange={(v) => { if (!v) { setPautaEn(null); setSustituyendo(null); } }}
        protocolo={pautaEn || {}}
        sustituyendo={sustituyendo}
        onSaved={cargar}
      />
      <LecturasDialog pauta={verLecturas} onOpenChange={setVerLecturas} />
    </div>
  );
}
